import React, { useContext } from 'react'
import { Radio, theme } from 'antd'

import { ThemeContext } from '../../../context/themeContext'
import { DARK_THEME } from '@vacationtracker/shared/constants/ThemeSetting'
import { hexToRgba } from '@vacationtracker/shared/functions/hex-to-rgba'

// Control + icon are each centered inside a box the height of the title's first line,
// so they align with the title AND with each other regardless of icon size.
const LINE = 22

// Static (token-independent) style objects, hoisted so they aren't re-allocated every render.
const SLOT: React.CSSProperties = { display: 'flex', alignItems: 'center', height: LINE, flexShrink: 0 }
const HEADER: React.CSSProperties = { display: 'flex', gap: 12, alignItems: 'flex-start', padding: 15 }
const TITLE_WRAP: React.CSSProperties = { flex: 1, minWidth: 0 }

export interface IExpandableCardProps {
  control?: React.ReactNode
  icon?: React.ReactNode
  title: React.ReactNode
  description?: React.ReactNode
  extra?: React.ReactNode
  active?: boolean
  disabled?: boolean
  expanded?: boolean
  onClick?: () => void
  children?: React.ReactNode
}

export const ExpandableCard = ({ control, icon, title, description, extra, active, disabled, expanded, onClick, children }: IExpandableCardProps) => {
  const { token } = theme.useToken()
  const { theme: appTheme } = useContext(ThemeContext)
  const isDark = appTheme === DARK_THEME
  const clickable = Boolean(onClick) && !disabled

  // The app configures no darkAlgorithm, so token.colorPrimaryBg isn't dark-safe. Derive the
  // active tint from colorPrimary so it reads correctly in both themes.
  const activeBg = hexToRgba(token.colorPrimary, isDark ? 0.24 : 0.1)

  return (
    <div
      role="presentation"
      onClick={() => clickable && onClick?.()}
      style={{
        border: `1px solid ${active ? token.colorPrimary : token.colorBorder}`,
        background: active ? activeBg : token.colorBgContainer,
        // Establish a base font-size for the whole card: an ancestor in this app sets
        // `font-size: 0` (whitespace-collapse trick), which would leave any child without an
        // explicit size - the title, or arbitrary reveal content - invisible.
        fontSize: 14,
        borderRadius: token.borderRadiusLG,
        marginBottom: token.marginSM,
        cursor: clickable ? 'pointer' : 'default',
        opacity: disabled ? 0.6 : 1,
        transition: 'border-color .15s, background .15s',
      }}
    >
      <div style={HEADER}>
        {control && <span style={SLOT}>{control}</span>}
        {icon && <span style={{ ...SLOT, color: active ? token.colorPrimary : token.colorTextSecondary, fontSize: 17 }}>{icon}</span>}
        <div style={TITLE_WRAP}>
          <div style={{ fontWeight: 600, fontSize: 15, lineHeight: `${LINE}px`, color: token.colorText }}>{title}</div>
          {description && <div style={{ color: token.colorTextSecondary, fontSize: 13 }}>{description}</div>}
        </div>
        {extra && <span style={SLOT}>{extra}</span>}
      </div>

      {/* Reveal region - grid-rows 0fr -> 1fr animates auto height. */}
      <div
        style={{
          display: 'grid',
          gridTemplateRows: expanded && children ? '1fr' : '0fr',
          transition: 'grid-template-rows .18s ease',
        }}
      >
        <div style={{ overflow: 'hidden' }}>
          {expanded && children && (
            <div onClick={(event) => event.stopPropagation()} style={{ padding: '14px 15px 15px', borderTop: `1px solid ${token.colorBorderSecondary}` }}>
              {children}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export interface IExpandableRadioOption {
  value: string | number | boolean
  icon?: React.ReactNode
  title: React.ReactNode
  description?: React.ReactNode
  disabled?: boolean
  expandContent?: React.ReactNode
}

interface IExpandableRadioGroupProps {
  value?: string | number | boolean
  onChange?: (value: string | number | boolean) => void
  onSelect?: (value: string | number | boolean) => void
  options: IExpandableRadioOption[]
  disabled?: boolean
}

/**
 * Radio-group composition of ExpandableCard. Works as an antd Form.Item child (consumes the
 * injected `value`/`onChange`). `onSelect` runs alongside `onChange` for side-effects. The card
 * body handles mouse selection; the real (pointer-events-none) Radio inside keeps Radio.Group
 * keyboard/a11y semantics without double-firing on click.
 */
export const ExpandableRadioGroup = ({ value, onChange, onSelect, options, disabled }: IExpandableRadioGroupProps) => {
  const select = (next: string | number | boolean) => {
    onChange?.(next)
    onSelect?.(next)
  }
  return (
    <Radio.Group value={value} disabled={disabled} onChange={(event) => select(event.target.value)} style={{ display: 'block', width: '100%' }}>
      {options.map((option) => {
        const optionDisabled = disabled || option.disabled
        return (
          <ExpandableCard
            key={String(option.value)}
            control={
              <span style={{ pointerEvents: 'none' }}>
                <Radio value={option.value} disabled={optionDisabled} />
              </span>
            }
            icon={option.icon}
            title={option.title}
            description={option.description}
            disabled={optionDisabled}
            active={value === option.value}
            expanded={value === option.value && Boolean(option.expandContent)}
            onClick={() => !optionDisabled && value !== option.value && select(option.value)}
          >
            {option.expandContent}
          </ExpandableCard>
        )
      })}
    </Radio.Group>
  )
}

export default ExpandableCard
